/**
 * Hook for managing price configuration with stability.
 * Provides memoized price formatting config that only updates
 * when the price magnitude changes significantly.
 */

import { useMemo, useRef, useCallback } from 'react';

import {
    getPriceConfig,
    shouldRecalculateConfig,
    DEFAULT_PRICE_CONFIG,
    type PriceConfig,
} from '../utils/priceFormat';

export type { PriceConfig };

/**
 * Hook that provides a stable PriceConfig based on a reference price.
 *
 * The config only updates when the order of magnitude of the price changes,
 * preventing unnecessary recalculations during normal price fluctuations.
 *
 * @param referencePrice - The reference price to base configuration on
 * @returns Stable PriceConfig object with formatting functions and thresholds
 *
 * @example
 * const priceConfig = usePriceConfig(currentPrice);
 * console.log(priceConfig.formatDisplay(currentPrice)); // "0.091410"
 */
export const usePriceConfig = (referencePrice: number): PriceConfig => {
    // Store the price that was used to calculate current config
    const configPriceRef = useRef<number>(referencePrice);
    // Store the current config
    const configRef = useRef<PriceConfig>(getPriceConfig(referencePrice));

    // Memoize the config, only recalculating when magnitude changes
    const priceConfig = useMemo(() => {
        // Check if we need to recalculate
        if (shouldRecalculateConfig(configPriceRef.current, referencePrice)) {
            configPriceRef.current = referencePrice;
            configRef.current = getPriceConfig(referencePrice);
        }
        return configRef.current;
    }, [referencePrice]);

    return priceConfig;
};

/**
 * Hook that provides price config with explicit reset capability.
 * Useful when you need to force a recalculation (e.g., token pair change).
 *
 * @param referencePrice - The reference price to base configuration on
 * @returns Object containing priceConfig and a reset function
 */
export const usePriceConfigWithReset = (
    referencePrice: number,
): {
    priceConfig: PriceConfig;
    resetConfig: () => void;
    hasConfigChanged: boolean;
} => {
    // Store the price that was used to calculate current config
    const configPriceRef = useRef<number>(referencePrice);
    // Store the current config
    const configRef = useRef<PriceConfig>(getPriceConfig(referencePrice));
    // Track if config changed in this render
    const hasConfigChangedRef = useRef<boolean>(false);

    // Memoize the config
    const priceConfig = useMemo(() => {
        hasConfigChangedRef.current = false;

        // Check if we need to recalculate
        if (shouldRecalculateConfig(configPriceRef.current, referencePrice)) {
            configPriceRef.current = referencePrice;
            configRef.current = getPriceConfig(referencePrice);
            hasConfigChangedRef.current = true;
        }

        return configRef.current;
    }, [referencePrice]);

    // Force reset function
    const resetConfig = useCallback(() => {
        configPriceRef.current = referencePrice;
        configRef.current = getPriceConfig(referencePrice);
    }, [referencePrice]);

    return {
        priceConfig,
        resetConfig,
        hasConfigChanged: hasConfigChangedRef.current,
    };
};

/**
 * Returns the default price config (based on ~$100 prices).
 * Useful as a fallback or for components that don't have access to price data.
 */
export const useDefaultPriceConfig = (): PriceConfig => {
    return useMemo(() => DEFAULT_PRICE_CONFIG, []);
};
